// Only used for loading in the related content ribbon. You'll know if you
// need it.
//
import { h, render } from 'preact';

import * as d3 from 'd3';
// import { voronoi as d3Voronoi } from 'd3-voronoi';
import debounce from './utils/debounce';
import AdLoader from './utils/ad-loader';
import Ribbon from './components/Ribbon';

const PRIMARY_GUTEN_TAG = 'subject-2018-elections';
const RELATED_CONTENT_URL = `https://www.texastribune.org/api/v1/content/?content_type=story,audio,video,pointer&tag=${PRIMARY_GUTEN_TAG}&tag!=object-tribcast&fields=id,url,readable_pub_date,headline,short_summary,lead_art&limit=3&format=json`;

export default () => {
  // Loads ribbon
  render(<Ribbon />, document.querySelector('#ribbon-container'));

  drawCharts();
  window.addEventListener('resize', debounce(drawCharts, 300));

  // triggers the drawing of the charts
  function drawCharts() {
    d3.selectAll('svg').remove();

    const maximumHeight = 300;
    const minimumWidth = 300;
    const margin = { top: 20, right: 30, bottom: 30, left: 60 };

    // sets up chart containers for each county
    d3.selectAll('.chart').each(function(d, i) {
      let county = d3.select(this).attr('data-county');
      let jsonURL = window.DATA[county];
      let demData = jsonURL[0].values;
      let repData = jsonURL[1].values;

      // draws a line chart for each party
      function partyChart(data, party) {
        const chartClass = '.' + county + '-' + party + '-chart';
        const thisEl = d3.select(chartClass);
        const container = document.getElementById('partyChart');
        const containerBox = container.getBoundingClientRect();
        const containerWidth = containerBox.width;

        /**
         * The ratio for determining the height of a chart on desktop devices.
         * @type {Number}
         */
        const DESKTOP_ASPECT_RATIO = 9 / 16;

        /**
         * The ratio for determining the height of a chart on mobile devices.
         * @type {Number}
         */
        const MOBILE_ASPECT_RATIO = 3 / 4;

        /**
         * The pivot point for altering charts based on width. Change this to alter
         * when a chart should change.
         * @type {Number}
         */
        const MOBILE_THRESHOLD = 480;

        // If the container is smaller than or equal to MOBILE_THRESHOLD, we are in
        // a mobile context
        const isMobile = containerWidth <= MOBILE_THRESHOLD ? true : false;

        // Determines the shape of the chart and is used to calculate the chart's
        // height
        const aspectRatio = isMobile
          ? MOBILE_ASPECT_RATIO
          : DESKTOP_ASPECT_RATIO;

        // The width of the chart
        const width = containerWidth - margin.left - margin.right;

        // The height of the chart, uses `aspectRatio` to calculate
        const height =
          containerWidth * aspectRatio - margin.top - margin.bottom;

        // var width = sizing.width - margin.left - margin.right;
        // var height = (width > maximumHeight ? maximumHeight : width) - margin.top - margin.bottom;

        const x = d3.scaleLinear().range([0, width]);
        let y = d3.scaleLinear().range([height, 0]);

        const bisectDate = d3.bisector(function(d) {
          return d.day;
        }).left;
        const commaFormat = d3.format(',');

        const xAxis = d3
          .axisBottom()
          .scale(x)
          .ticks(11);

        let yAxis = d3
          .axisLeft()
          .scale(y)
          .tickSize(-width)
          .ticks(6);

        let countDataline = d3
          .line()
          .x(function(d) {
            return x(d.day);
          })
          .y(function(d) {
            return y(d.cumulative_in_person);
          });

        let vapDataline = d3
          .line()
          .x(function(d) {
            return x(d.day);
          })
          .y(function(d) {
            return y(d.per);
          });

        const svg = d3
          .select(chartClass)
          .append('svg')
          .attr('width', width + margin.left + margin.right)
          .attr('height', height + margin.top + margin.bottom)
          .style('overflow', 'visible')
          .append('g')
          .attr(
            'transform',
            'translate(' + margin.left + ',' + margin.top + ')'
          );

        // finds the biggest between the rep and dem vote totals or percents
        let findMaxCount = [
          d3.max(repData, function(d) {
            return d.cumulative_in_person;
          }),
          d3.max(demData, function(d) {
            return d.cumulative_in_person;
          }),
        ];
        let findMaxVap = [
          d3.max(repData, function(d) {
            return d.per;
          }),
          d3.max(demData, function(d) {
            return d.per;
          }),
        ];

        // this'll always be 1-11 days
        x.domain([1, 11]);
        y.domain([0, d3.max(findMaxCount)]).nice();
        // Add the X Axis
        svg
          .append('g')
          .attr('class', 'x-axis axis')
          .attr('transform', 'translate(0,' + height + ')')
          .call(xAxis)
          .append('text')
          .attr('x', -18)
          .attr('y', 18)
          .attr('class', 'x-label')
          .style('text-anchor', 'middle')
          .text('Day');

        // Add the Y Axis
        let yTicks = svg
          .append('g')
          .attr('class', 'y-axis axis y-axis-' + county)
          .call(yAxis);

        // creates new object for each year
        const dataNest = d3
          .nest()
          .key(function(d) {
            return d.year;
          })
          .entries(data);

        const years = svg
          .selectAll('.year')
          .data(dataNest)
          .enter()
          .append('g')
          .attr('class', 'year');

        // adds a line for each year
        years
          .append('path')
          .attr('class', function(d) {
            return (
              'line line' +
              d.key +
              ' ' +
              party +
              'line' +
              d.key +
              ' line' +
              county
            );
          })
          .attr('d', function(d) {
            return countDataline(d.values);
          });

        const firstYTick = yTicks.select('.tick:first-of-type');
        firstYTick.select('text').text('');

        let lastYTick = yTicks.select('.tick:last-of-type');

        // text and position for y axis label
        let title = lastYTick
          .append('text')
          .attr('x', 67)
          .attr('dy', '.32em')
          .text('early voters');

        lastYTick.select('line').attr('x1', title.node().getBBox().width + 7);

        // sets up dynamic HTML for tooltip
        var tooltipClass = '.' + party + '-' + county + '-' + 'tooltip';
        var tooltip = thisEl.select(tooltipClass).style('display', 'none');
        var tooltipDay = tooltip.select('.tooltip__day');
        var tooltipYears = {
          2018: tooltip.select('.tooltip__2018'),
          2014: tooltip.select('.tooltip__2014'),
          2010: tooltip.select('.tooltip__2010'),
        };

        // append a circle for each year to go along with the tooltip
        var circle2010 = svg
          .append('circle')
          .attr('r', 4)
          .attr('class', party + 'circle2010 circle2010' + county + party)
          .style('display', 'none');
        var circle2014 = svg
          .append('circle')
          .attr('r', 4)
          .attr('class', party + 'circle2014 circle2014' + county + party)
          .style('display', 'none');
        var circle2018 = svg
          .append('circle')
          .attr('r', 4)
          .attr('class', party + 'circle2018 circle2018' + county + party)
          .style('display', 'none');

        // adds overlay to each chart for tooltip to float over
        // set up mouseover functionality
        svg
          .append('rect')
          .attr('class', 'overlay overlay-' + county + party)
          .attr('data-county', county)
          .attr('data-party', party)
          .attr('width', width)
          .attr('height', height)
          .on('mouseover', function(d) {
            tooltip.style('display', null);
            circle2010.style('display', null);
            circle2014.style('display', null);
            circle2018.style('display', null);
          })
          .on('mouseout', function() {
            tooltip.style('display', 'none');
            circle2010.style('display', 'none');
            circle2014.style('display', 'none');
            circle2018.style('display', 'none');
          })
          .on('mousemove', function() {
            mousemove(this, 'cumulative_in_person');
          });

        // Mouseover functionality for tooltip
        function mousemove(e, i_data) {
          var county = e.getAttribute('data-county');
          var party = e.getAttribute('data-party');
          var county_json = window.DATA[county];

          if (party == 'dem') {
            var data = county_json[0].values;
          } else {
            var data = county_json[1].values;
          }

          var dataNest = d3
            .nest()
            .key(function(d) {
              return d.year;
            })
            .entries(data);

          // magic telling tooltip where to go based on where your mouse is
          var x0 = x.invert(d3.mouse(e)[0]);
          var i = x0 >= 14 ? 15 : bisectDate(dataNest[0].values, x0, 1);
          var d0 = data[i - 1];
          var d1 = data[i];
          var d = x0 - d0.day > d1.day - x0 ? d1 : d0;
          // creating datanest to parse out years for tooltip
          var output = dataNest.map(function(year) {
            // prevents it from being super mad if there isn't data for a particular day
            if (year.values.length < d.day) {
              return { year: year.key, [i_data]: null };
            } else {
              return year.values[d.day - 1];
            }
          });

          // empty array to collect cumulative in person counts
          // so the tooltip can be positioned at highest y value
          // for each day
          var highestCount = [];
          // loops through values of each year to plug data into tooltip
          output.forEach(function(year) {
            // pushes cumulative in person numbers to empty array
            highestCount.push(year[i_data]);
            // sets text for tooltip
            function cleanNumber(data) {
              if (data === 'cumulative_in_person') {
                return tooltipYears[year.year].text(
                  `${year.year}: ${year[data] ? commaFormat(year[data]) : '-'}`
                );
              } else if (data === 'per') {
                var round = d3.format('.1f');
                var roundPercents = round(year[data]);
                return tooltipYears[year.year].text(
                  `${year.year}: ${year[data] ? round(year[data]) + '%' : '-'}`
                );
              }
            }
            cleanNumber(i_data);

            // positions circles for each line
            svg
              .select('.circle' + year.year + county + party)
              .attr(
                'transform',
                'translate(' + x(d.day) + ',' + y(year[i_data]) + ')'
              );
            // if there isn't data for a day, hide the circle
            if (year[i_data] === null) {
              svg
                .select('.circle' + year.year + county + party)
                .style('display', 'none');
            } else {
              svg
                .select('.circle' + year.year + county + party)
                .style('display', null);
            }
          });
          // sorts the array of cumulative in person values, smallest to largest
          highestCount.sort(function(a, b) {
            return a - b;
          });

          // finds largest cumulative in person number
          var yPosition;
          if (highestCount[2] === null) {
            yPosition = highestCount[1];
          } else {
            yPosition = highestCount[2];
          }

          tooltipDay.text(`Day ${d.day}`);
          tooltip.style('left', d3.event.pageX - 34 + 'px');
          // positions tooltip at highest y value for each day
          tooltip.style('top', d3.event.pageY - 175 + 'px');
        } // mousemove close

        d3.selectAll('input').on('change', change);

        function change() {
          if (this.value === 'vap') {
            d3.selectAll('.chart').each(function(d, i) {
              county = d3.select(this).attr('data-county');
              jsonURL = window.DATA[county];
              demData = jsonURL[0].values;
              repData = jsonURL[1].values;

              findMaxVap = [
                d3.max(repData, function(d) {
                  return d.per;
                }),
                d3.max(demData, function(d) {
                  return d.per;
                }),
              ];
              vapDataline = d3
                .line()
                .x(function(d) {
                  return x(d.day);
                })
                .y(function(d) {
                  return y(d.per);
                });

              y.domain([0, d3.max(findMaxVap)]).nice();

              yAxis = d3
                .axisLeft()
                .scale(y)
                .tickSize(-width)
                .tickFormat(function(d, i, x) {
                  if (i === x.length - 1) {
                    return d + '%';
                  } else if (i === 0) {
                    return '';
                  } else {
                    return d;
                  }
                })
                .ticks(6);

              d3.selectAll('.y-axis-' + county).call(yAxis);

              d3.selectAll('.line' + county).attr('d', function(d) {
                return vapDataline(d.values);
              });

              lastYTick = d3
                .selectAll('.y-axis-' + county)
                .select('.tick:last-of-type');

              title = lastYTick
                .append('text')
                .attr('x', 168)
                .attr('dy', '.32em')
                .text('of voting age population (18+)');

              lastYTick
                .select('line')
                .attr('x1', title.node().getBBox().width + 13);

              // sets up dynamic HTML for tooltip
              tooltipClass = '.' + party + '-' + county + '-' + 'tooltip';
              tooltip = thisEl.select(tooltipClass).style('display', 'none');
              tooltipDay = tooltip.select('.tooltip__day');
              tooltipYears = {
                2018: tooltip.select('.tooltip__2018'),
                2014: tooltip.select('.tooltip__2014'),
                2010: tooltip.select('.tooltip__2010'),
              };

              // append a circle for each year to go along with the tooltip
              circle2010 = svg
                .selectAll('.circle2010' + county + party)
                .style('display', 'none');
              circle2014 = svg
                .selectAll('.circle2014' + county + party)
                .style('display', 'none');
              circle2018 = svg
                .selectAll('.circle2018' + county + party)
                .style('display', 'none');

              d3
                .selectAll('.overlay')
                // .attr('width', width)
                // .attr('height', height)
                .on('mouseover', function(d) {
                  tooltip.style('display', null);
                  circle2010.style('display', null);
                  circle2014.style('display', null);
                  circle2018.style('display', null);
                })
                .on('mouseout', function() {
                  tooltip.style('display', 'none');
                  circle2010.style('display', 'none');
                  circle2014.style('display', 'none');
                  circle2018.style('display', 'none');
                })
                .on('mousemove', function() {
                  mousemove(this, 'per');
                });
            });
          } else if (this.value === 'count') {
            d3.selectAll('.chart').each(function(d, i) {
              county = d3.select(this).attr('data-county');
              jsonURL = window.DATA[county];
              demData = jsonURL[0].values;
              repData = jsonURL[1].values;

              findMaxCount = [
                d3.max(repData, function(d) {
                  return d.cumulative_in_person;
                }),
                d3.max(demData, function(d) {
                  return d.cumulative_in_person;
                }),
              ];

              countDataline = d3
                .line()
                .x(function(d) {
                  return x(d.day);
                })
                .y(function(d) {
                  return y(d.cumulative_in_person);
                });

              y.domain([0, d3.max(findMaxCount)]).nice();

              yAxis = d3
                .axisLeft()
                .scale(y)
                .tickSize(-width)
                .ticks(6);

              d3.selectAll('.y-axis-' + county).call(yAxis);

              d3.selectAll('.line' + county).attr('d', function(d) {
                return countDataline(d.values);
              });

              lastYTick = d3
                .selectAll('.y-axis-' + county)
                .select('.tick:last-of-type');

              title = lastYTick
                .append('text')
                .attr('x', 67)
                .attr('dy', '.32em')
                .text('early voters');

              lastYTick
                .select('line')
                .attr('x1', title.node().getBBox().width + 7);

              // sets up dynamic HTML for tooltip
              tooltipClass = '.' + party + '-' + county + '-' + 'tooltip';
              tooltip = thisEl.select(tooltipClass).style('display', 'none');
              tooltipDay = tooltip.select('.tooltip__day');
              tooltipYears = {
                2018: tooltip.select('.tooltip__2018'),
                2014: tooltip.select('.tooltip__2014'),
                2010: tooltip.select('.tooltip__2010'),
              };

              // append a circle for each year to go along with the tooltip
              circle2010 = svg
                .selectAll('.circle2010' + county + party)
                .style('display', 'none');
              circle2014 = svg
                .selectAll('.circle2014' + county + party)
                .style('display', 'none');
              circle2018 = svg
                .selectAll('.circle2018' + county + party)
                .style('display', 'none');

              d3
                .selectAll('.overlay')
                // .attr('width', width)
                // .attr('height', height)
                .on('mouseover', function(d) {
                  tooltip.style('display', null);
                  circle2010.style('display', null);
                  circle2014.style('display', null);
                  circle2018.style('display', null);
                })
                .on('mouseout', function() {
                  tooltip.style('display', 'none');
                  circle2010.style('display', 'none');
                  circle2014.style('display', 'none');
                  circle2018.style('display', 'none');
                })
                .on('mousemove', function() {
                  mousemove(this, 'cumulative_in_person');
                });
            });
          }
        }
      } // partychart close

      partyChart(demData, 'dem');
      partyChart(repData, 'rep');
    });
  }

  // ads
  const loader = new AdLoader({
    targetingKey: 'guten',
    targetingValue: PRIMARY_GUTEN_TAG,
  });

  loader.init();
};



// WEBPACK FOOTER //
// ./main.js